import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, type CaptureJob, type CaptureMode, type CaptureStartBody } from "../api";

type ToastFn = (kind: "success" | "error" | "info", message: string) => void;
type Props = { showToast: ToastFn };

const MODE_INFO: Record<CaptureMode, { label: string; hint: string }> = {
  manual: {
    label: "🖱️ Manuelle Aufnahme",
    hint: "Sichtbares Fenster — DU bedienst die Seite selbst mit der Maus. Wir schneiden live mit, die Maus ist im Video nicht sichtbar. Stoppen per Knopf.",
  },
  video: {
    label: "🎬 Scroll-Recording",
    hint: "Automatisches, gleichmäßiges Scroll-Video. Tempo über Dauer & Scroll-Distanz.",
  },
  fullpage: {
    label: "🖼️ Langes Seiten-Bild",
    hint: "Ein durchgehender, langer Screenshot (für scrollende Standbilder in screenScrollPan).",
  },
  viewport: {
    label: "📱 Einzel-Screen",
    hint: "Ein sauberer Screenshot des sichtbaren Bereichs.",
  },
  screens: {
    label: "🪜 Mehrere Screens",
    hint: "Screenshots an mehreren Scroll-Positionen (Fraktionen 0..1).",
  },
  flow: {
    label: "👆 Klick-Durchlauf",
    hint: "Pro Schritt ein Screenshot nach simuliertem Klick. text=… klickt per Button-Text.",
  },
};

const DEVICES = [
  { id: "phone", label: "📱 Phone (9:16)" },
  { id: "tablet", label: "📲 Tablet" },
  { id: "desktop", label: "🖥️ Desktop (16:9)" },
];

type Form = {
  url: string;
  mode: CaptureMode;
  device: string;
  out: string;
  duration: number;
  fps: number;
  maxDuration: number;
  scrollPct: number;
  stops: string;
  clicks: string;
  cookie: string;
  hide: string;
  hideSticky: boolean;
};

const DEFAULT_FORM: Form = {
  url: "https://allyver.com",
  mode: "manual",
  device: "phone",
  out: "",
  duration: 8,
  fps: 24,
  maxDuration: 90,
  scrollPct: 60,
  stops: "0,0.5,1",
  clicks: "",
  cookie: "",
  hide: "",
  hideSticky: false,
};

export const Capture: React.FC<Props> = ({ showToast }) => {
  const [form, setForm] = useState<Form>(DEFAULT_FORM);
  const [job, setJob] = useState<CaptureJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [, setTick] = useState(0);
  const logRef = useRef<HTMLPreElement | null>(null);

  const set = <K extends keyof Form>(k: K, v: Form[K]): void => setForm((f) => ({ ...f, [k]: v }));

  const running = job?.status === "running";

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const r = await api.captureStatus();
      setJob(r.job);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll while a job is running + keep the elapsed timer ticking.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setTick((t) => t + 1);
      void refresh();
    }, 1000);
    return () => window.clearInterval(id);
  }, [running, refresh]);

  // Auto-scroll log to bottom.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [job?.log?.length]);

  const onStart = async (): Promise<void> => {
    setStarting(true);
    try {
      const body: CaptureStartBody = {
        url: form.url.trim(),
        mode: form.mode,
        device: form.device,
        out: form.out.trim() || undefined,
        cookie: form.cookie.trim() || undefined,
        hide: form.hide.trim() || undefined,
        hideSticky: form.hideSticky || undefined,
      };
      if (form.mode === "video") {
        body.duration = form.duration;
        body.fps = form.fps;
        body.scrollPct = form.scrollPct;
      } else if (form.mode === "manual") {
        body.fps = form.fps;
        body.maxDuration = form.maxDuration;
      } else if (form.mode === "fullpage" || form.mode === "viewport") {
        body.scrollPct = form.scrollPct;
      } else if (form.mode === "screens") {
        body.stops = form.stops.trim() || undefined;
      } else if (form.mode === "flow") {
        body.clicks = form.clicks.trim() || undefined;
      }
      const r = await api.captureStart(body);
      setJob(r.job);
      if (form.mode === "manual") {
        showToast("info", "Browserfenster öffnet sich — bediene die Seite mit der Maus. Zum Speichern hier auf „Stoppen“ klicken.");
      } else {
        showToast("info", "Aufnahme gestartet …");
      }
    } catch (e) {
      showToast("error", `Start fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setStarting(false);
    }
  };

  const onStop = async (): Promise<void> => {
    try {
      const r = await api.captureStop();
      setJob(r.job);
      showToast("info", "Aufnahme wird finalisiert (Video wird zusammengebaut) …");
    } catch (e) {
      showToast("error", `Stop fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  const onCancel = async (): Promise<void> => {
    try {
      const r = await api.captureCancel();
      setJob(r.job);
      showToast("info", "Aufnahme abgebrochen.");
    } catch (e) {
      showToast("error", `Abbruch fehlgeschlagen: ${(e as Error).message}`);
    }
  };

  // Notify on completion transition.
  const prevStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = job?.status;
    if (s && s !== prevStatus.current) {
      if (s === "done") showToast("success", `Fertig! ${job?.results.length ?? 0} Asset(s) in der Upload-Library.`);
      else if (s === "failed") showToast("error", `Aufnahme fehlgeschlagen: ${job?.error ?? "unbekannt"}`);
    }
    prevStatus.current = s;
  }, [job?.status, job?.results.length, job?.error, showToast]);

  const elapsed = job ? Math.round(((job.finishedAt ?? Date.now()) - job.startedAt) / 1000) : 0;
  const info = MODE_INFO[form.mode];

  const statusColor =
    job?.status === "running" ? "#f59e0b"
    : job?.status === "done" ? "#22c55e"
    : job?.status === "failed" ? "#ef4444"
    : "#94a3b8";

  return (
    <div className="workspace">
      <div className="workspace-head">
        <div className="workspace-head-left">
          <h2>Capture · Screenshots & Recordings</h2>
          <p className="workspace-sub">
            Nimm <strong>saubere</strong> Screenshots und Screen-Recordings direkt aus dem Studio auf — ganz ohne
            Browser-/URL-Leiste. Die Assets landen sofort in der Upload-Library und sind in Szenen wie{" "}
            <code>screenScrollPan</code> & <code>appFlowWalkthrough</code> nutzbar.
          </p>
        </div>
        <div className="workspace-head-actions">
          <button className="btn ghost" onClick={() => void refresh()}>↻ Status</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(360px, 460px) 1fr", gap: 16, alignItems: "start" }}>
        {/* ---------------- Form ---------------- */}
        <section style={panelStyle}>
          <div className="field">
            <label>URL</label>
            <input
              type="text"
              value={form.url}
              onChange={(e) => set("url", e.target.value)}
              placeholder="https://allyver.com"
              disabled={running}
            />
          </div>

          <div className="field">
            <label>Modus</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {(Object.keys(MODE_INFO) as CaptureMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => set("mode", m)}
                  disabled={running}
                  className={`chip ${form.mode === m ? "active" : ""}`}
                  style={{ justifyContent: "flex-start", textAlign: "left" }}
                >
                  {MODE_INFO[m].label}
                </button>
              ))}
            </div>
            <div className="helper" style={{ marginTop: 6 }}>{info.hint}</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="field">
              <label>Gerät / Format</label>
              <select value={form.device} onChange={(e) => set("device", e.target.value)} disabled={running}>
                {DEVICES.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Dateiname (optional)</label>
              <input
                type="text"
                value={form.out}
                onChange={(e) => set("out", e.target.value)}
                placeholder="z. B. allyver-feature-x"
                disabled={running}
              />
            </div>
          </div>

          {/* Mode-specific controls */}
          {form.mode === "manual" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <NumField label="Bildrate (fps)" value={form.fps} min={10} max={30} onChange={(v) => set("fps", v)} disabled={running} />
              <NumField label="Max. Dauer (s)" value={form.maxDuration} min={5} max={600} onChange={(v) => set("maxDuration", v)} disabled={running} />
            </div>
          )}
          {form.mode === "video" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <NumField label="Dauer (s)" value={form.duration} min={2} max={60} onChange={(v) => set("duration", v)} disabled={running} />
              <NumField label="Bildrate (fps)" value={form.fps} min={10} max={60} onChange={(v) => set("fps", v)} disabled={running} />
              <NumField label="Scroll %" value={form.scrollPct} min={0} max={100} onChange={(v) => set("scrollPct", v)} disabled={running} />
            </div>
          )}
          {(form.mode === "fullpage" || form.mode === "viewport") && (
            <NumField label="Scroll % (Distanz)" value={form.scrollPct} min={0} max={100} onChange={(v) => set("scrollPct", v)} disabled={running} />
          )}
          {form.mode === "screens" && (
            <div className="field">
              <label>Scroll-Stops (0..1, kommagetrennt)</label>
              <input type="text" value={form.stops} onChange={(e) => set("stops", e.target.value)} placeholder="0,0.5,1" disabled={running} />
            </div>
          )}
          {form.mode === "flow" && (
            <div className="field">
              <label>Klick-Schritte (mit ; getrennt)</label>
              <input type="text" value={form.clicks} onChange={(e) => set("clicks", e.target.value)} placeholder="text=Händler;text=Produkte" disabled={running} />
            </div>
          )}

          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: "pointer", fontSize: 13, opacity: 0.75 }}>Erweitert</summary>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="field">
                <label>Cookie-Button-Text (optional)</label>
                <input type="text" value={form.cookie} onChange={(e) => set("cookie", e.target.value)} placeholder="(automatisch erkannt)" disabled={running} />
              </div>
              <div className="field">
                <label>CSS-Selektoren ausblenden (optional)</label>
                <input type="text" value={form.hide} onChange={(e) => set("hide", e.target.value)} placeholder=".cookie-banner, #chat-widget" disabled={running} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={form.hideSticky} onChange={(e) => set("hideSticky", e.target.checked)} disabled={running} />
                Fixe/sticky Elemente ausblenden
              </label>
            </div>
          </details>

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            {!running ? (
              <button className="btn primary" onClick={() => void onStart()} disabled={starting || !form.url.trim()}>
                {starting ? "…" : form.mode === "manual" ? "🖱️ Aufnahme starten" : "● Aufnahme starten"}
              </button>
            ) : (
              <>
                {form.mode === "manual" || job?.mode === "manual" ? (
                  <button className="btn primary" onClick={() => void onStop()} style={{ background: "#ef4444", borderColor: "#ef4444" }}>
                    ⏹ Stoppen & speichern
                  </button>
                ) : null}
                <button className="btn danger" onClick={() => void onCancel()}>Abbrechen</button>
              </>
            )}
          </div>
        </section>

        {/* ---------------- Status / Result ---------------- */}
        <section style={panelStyle}>
          {!job ? (
            <div style={{ opacity: 0.6, fontSize: 14 }}>
              Noch keine Aufnahme. Wähle einen Modus und klicke „Aufnahme starten“.
              {" "}Beim <strong>manuellen</strong> Modus öffnet sich ein Browserfenster — bediene die Seite mit der Maus
              und klicke dann hier auf „Stoppen & speichern“.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: 99, background: statusColor, boxShadow: running ? `0 0 12px ${statusColor}` : "none" }} />
                <strong style={{ textTransform: "capitalize" }}>{job.status}</strong>
                <span style={{ opacity: 0.6, fontSize: 13 }}>· {job.mode} · {job.device} · {elapsed}s</span>
                {running && (job.mode === "manual") ? (
                  <span className="badge" style={{ marginLeft: "auto", background: "rgba(245,158,11,0.2)", color: "#f59e0b" }}>
                    ⏺ Aufnahme läuft — bediene das Browserfenster
                  </span>
                ) : null}
              </div>

              {job.results.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                  {job.results.map((r) => (
                    <div key={r.url} style={{ border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: 8, maxWidth: 220 }}>
                      {r.kind === "video" ? (
                        <video src={r.url} controls loop muted style={{ width: 200, borderRadius: 8, display: "block", background: "#000" }} />
                      ) : (
                        <img src={r.url} alt={r.name} style={{ width: 200, borderRadius: 8, display: "block", maxHeight: 360, objectFit: "cover" }} />
                      )}
                      <div style={{ fontSize: 11, opacity: 0.7, margin: "6px 2px 4px", wordBreak: "break-all" }}>{r.name}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn sm ghost"
                          onClick={() => {
                            void navigator.clipboard.writeText(r.url);
                            showToast("success", "Media-URL kopiert.");
                          }}
                        >
                          URL kopieren
                        </button>
                        <a className="btn sm ghost" href={r.url} download={r.name} target="_blank" rel="noreferrer">Download</a>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {job.recommendedScrollPct !== undefined && (
                <div style={{ fontSize: 13, color: "#93c5fd", marginBottom: 10 }}>
                  Tipp: empfohlener <code>extras.scrollPct</code> (Phone-Frame): <strong>{job.recommendedScrollPct}</strong>
                </div>
              )}

              {job.error && (
                <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5", padding: "8px 12px", borderRadius: 10, marginBottom: 10, fontSize: 13 }}>
                  {job.error}
                </div>
              )}

              <pre
                ref={logRef}
                style={{
                  background: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 10,
                  padding: 12,
                  fontSize: 12,
                  lineHeight: 1.5,
                  maxHeight: 320,
                  overflow: "auto",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                }}
              >
                {job.log.length ? job.log.join("\n") : "…"}
              </pre>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const panelStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 16,
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const NumField: React.FC<{ label: string; value: number; min?: number; max?: number; onChange: (v: number) => void; disabled?: boolean }> = ({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}) => (
  <div className="field">
    <label>{label}</label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
    />
  </div>
);
